import React, { FunctionComponent } from "react";

import {
  useCommunityContext,
  useCommunityByWpSiteId,
} from "@voxmedia/duet-context/context/community-context";
import { getResolvedHref } from "@voxmedia/duet-utils/url";

interface Props {
  href: string;
  hrefSiteData?: {
    isCrossCommunity: boolean;
    wpId: number;
  } | null;
  className?: string;
  suppressHydrationWarning?: boolean;
}

/**
 * DuetLink is a standard anchor link. This will automatically strip the community
 * origin off of href passed in that is the current community and should be relative.
 * It will correctly resolve the href to the target community if it is cross-community.
 * It will also correctly construct same community or cross-community hrefs using
 * the correct origin when in non-production environments.
 */
const DuetLink: FunctionComponent<
  Props & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">
> = ({ href, hrefSiteData, suppressHydrationWarning = false, ...props }) => {
  const { currentOrigin, currentCommunity } = useCommunityContext();
  const currentApexDomain = currentCommunity.domain;
  const hrefCommunity = useCommunityByWpSiteId(hrefSiteData?.wpId);

  const finalHref = getResolvedHref({
    href,
    currentOrigin,
    currentApexDomain,
    isCrossCommunity: hrefCommunity && hrefSiteData?.isCrossCommunity,
    hrefCommunityDomain: hrefCommunity?.domain,
    hrefCommunitySubdomain: hrefCommunity?.subdomain,
  });

  return (
    <a
      {...props}
      href={finalHref}
      suppressHydrationWarning={suppressHydrationWarning}
      className={props.className}
    >
      {props.children}
    </a>
  );
};

export default DuetLink;
